import type { EnumUnion } from "@dresscode-frontend/utility-types";
import { Link, createFileRoute, useNavigate } from "@tanstack/react-router";
import type { Dispatch, SetStateAction } from "react";
import { useCallback, useState } from "react";
import { match } from "ts-pattern";

import { AccordionButton } from "@/components/Button/AccordionButton";
import ActionButton from "@/components/Button/ActionButton";
import { AttributeCard } from "@/components/Card/AttributeCard";
import Icon from "@/components/Icon/Icon";
import type { IconName } from "@/components/Icon/iconMap";
import { Grid, HStack, Spacer, VStack } from "@/components/Layout";
import StepperHeaderComponent from "@/components/StepperHeader";
import { Text } from "@/components/Typography/Text";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { DeviceType } from "@/constants/device";
import { useDeviceType } from "@/features/device/device-type/useDeviceType";
import { defaultNS, useTranslation } from "@/lib/i18n";

import { AddDeviceStepKeys } from "./-constants/constant";

export const Route = createFileRoute("/_staff/devices/add/")({
  component: Introduce,
});

type CommonProps = {
  selectedDeviceType: DeviceType | null;
  setSelectedDeviceType: Dispatch<SetStateAction<DeviceType | null>>;
};

function Introduce() {
  const [selectedDeviceType, setSelectedDeviceType] = useState<EnumUnion<
    typeof DeviceType
  > | null>(DeviceType.COMPUTER);

  return (
    <VStack className="mx-auto w-[1000px] ">
      <Header />
      <Spacer variant="xs" />
      <DeviceTypeRadioGroup
        selectedDeviceType={selectedDeviceType}
        setSelectedDeviceType={setSelectedDeviceType}
      />
      <Spacer variant="xxl" />
      <AdditionalOptions
        selectedDeviceType={selectedDeviceType}
        setSelectedDeviceType={setSelectedDeviceType}
      />
    </VStack>
  );
}

function Header() {
  const { t } = useTranslation(defaultNS, {
    keyPrefix: "pages.devices.add.intro.header",
  });

  return (
    <VStack>
      <Spacer variant="xxl" />
      <StepperHeaderComponent headerTitle={t("headerTitle")} />
    </VStack>
  );
}

function DeviceTypeRadioGroup({
  selectedDeviceType,
  setSelectedDeviceType,
}: CommonProps) {
  const { t } = useTranslation(defaultNS, {
    keyPrefix: "pages.devices.add.intro.body",
  });
  const { t: tDeviceType } = useDeviceType();

  const DEVICE_TYPES: {
    id: DeviceType;
    title: string;
    requiredDetails: { iconName: IconName; text: string }[];
  }[] = [
    {
      id: DeviceType.COMPUTER,
      title: tDeviceType("computer"),
      requiredDetails: [
        { iconName: "SquareDot", text: t("itemCard.deviceCategory") },
        { iconName: "SquareDot", text: t("itemCard.deviceName") },
      ],
    },
    {
      id: DeviceType.MOBILE,
      title: tDeviceType("mobile"),
      requiredDetails: [
        { iconName: "SquareDot", text: t("itemCard.deviceCategory") },
        { iconName: "SquareDot", text: t("itemCard.deviceName") },
      ],
    },
    {
      id: DeviceType.MONITOR,
      title: tDeviceType("monitor"),
      requiredDetails: [
        { iconName: "SquareDot", text: t("itemCard.deviceName") },
      ],
    },
    {
      id: DeviceType.SERVER,
      title: tDeviceType("server"),
      requiredDetails: [
        { iconName: "SquareDot", text: t("itemCard.deviceName") },
      ],
    },
    {
      id: DeviceType.OTHER,
      title: tDeviceType("other"),
      requiredDetails: [
        { iconName: "SquareDot", text: t("itemCard.deviceName") },
      ],
    },
  ];

  const navigate = useNavigate();

  const handleRadioChange = useCallback(
    (value: DeviceType) => {
      setSelectedDeviceType(value);
    },
    [setSelectedDeviceType],
  );

  const handleButtonClick = useCallback(() => {
    if (selectedDeviceType) {
      navigate({
        to: `/devices/add/${selectedDeviceType}`,
        hash: AddDeviceStepKeys.BASIC,
      });
    }
  }, [selectedDeviceType, navigate]);

  return (
    <>
      <Spacer variant="xl" />
      <Text variant="h4"> {t("selectDeviceType")}</Text>
      <Spacer variant="l" />
      <RadioGroup onValueChange={handleRadioChange}>
        <Grid columns={2} rowGap={28} columnGap={24}>
          {DEVICE_TYPES.map((type) => (
            <AttributeCard
              key={type.id}
              title={type.title}
              titleStyle="CardHeader"
              onClick={() => handleRadioChange(type.id)}
              actionButton={
                <RadioGroupItem
                  id={type.id}
                  value={type.id}
                  className="h-4 w-4"
                  checked={selectedDeviceType === type.id}
                />
              }
              actionButtonPosition="top-left"
              actionButtonIsInner={false}
              className="cursor-pointer"
            >
              <VStack className="gap-3">
                <Text variant="labelSmall">
                  {t("itemCard.requiredDetails")}
                </Text>
                {type.requiredDetails.map((detail, index) => (
                  <Icon
                    key={`${detail.text}-${index}`}
                    iconName={detail.iconName}
                    text={detail.text}
                  />
                ))}
              </VStack>
            </AttributeCard>
          ))}
        </Grid>
      </RadioGroup>
      <Spacer variant="xl" />
      <ActionButton
        icon="FilePenIcon"
        className="w-48"
        onClick={handleButtonClick}
      >
        {t("fillOutDetails")}
      </ActionButton>
    </>
  );
}

function AdditionalOptions({ selectedDeviceType }: CommonProps) {
  const { t } = useTranslation(defaultNS, {
    keyPrefix: "pages.devices.add.intro.options",
  });
  const { t: tDeviceType } = useDeviceType();
  const navigate = useNavigate();

  const displayDeviceType = match(selectedDeviceType)
    .with(DeviceType.COMPUTER, () => tDeviceType("computer"))
    .with(DeviceType.MOBILE, () => tDeviceType("mobile"))
    .with(DeviceType.MONITOR, () => tDeviceType("monitor"))
    .with(DeviceType.SERVER, () => tDeviceType("server"))
    .with(DeviceType.OTHER, () => tDeviceType("other"))
    .with(null, () => "")
    .exhaustive();

  const handleSyncIT = () => {
    navigate({
      to: "/devices/external-it-saas",
    });
  };

  return (
    <VStack className="w-full">
      <Text variant="labelSemiLarge">{t("description")}</Text>
      <Spacer size={24} />
      <ActionButton
        variant="ghost"
        className="relative w-full justify-start p-0"
        onClick={handleSyncIT}
      >
        <HStack className="items-center justify-between w-full">
          <Icon
            iconName="DatabaseBackup"
            text={t("syncIT")}
            textColor="text-primary"
            colorVariant="primary"
          />
          <Icon iconName="ChevronRight" className="m-[10px]" />
        </HStack>
      </ActionButton>
      <Spacer size={24} />
      <AccordionButton
        icon={
          <Icon
            iconName="DatabaseBackup"
            text={t("bulkCsvUpload.title")}
            textColor="text-primary"
            colorVariant="primary"
          />
        }
        className="w-full"
      >
        <VStack className="gap-2">
          <div className="flex flex-col items-start gap-2 text-neutral-on-fill">
            <Text variant="p">{t("bulkCsvUpload.description")}</Text>
            <Text variant="label">{`${t("bulkCsvUpload.selectedDeviceType")} ${displayDeviceType}`}</Text>
          </div>
          <Link
            to={`/devices/import-csv/$deviceType`}
            params={{ deviceType: selectedDeviceType ?? DeviceType.COMPUTER }}
            hash="upload-file"
          >
            <ActionButton
              size="sm"
              variant="default"
              icon="ArrowRightIcon"
              iconPosition="right"
            >
              {t("bulkCsvUpload.button")}
            </ActionButton>
          </Link>
        </VStack>
      </AccordionButton>
    </VStack>
  );
}
